﻿import type { WaykeLocation, WaykeQueryLocation } from '../@types/WaykeLocation';
import { getMarket } from '../i18n';
import { getLocationSet } from '../utils/maps';
import { slugify } from './urls';

const toRepresentation = (queryString: string) =>
  typeof Buffer !== 'undefined' ? Buffer.from(queryString).toString('base64') : btoa(queryString);

const fromRepresentation = (raw: string) =>
  typeof Buffer !== 'undefined' ? Buffer.from(raw, 'base64').toString() : atob(raw);

export const queryString = (name: string, url?: string) => {
  if (!process.env.SSR) {
    url = url || window.location.search;
    name = name.replace(/([\\[\]])/g, '\\$1');
    const re = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
    const results = re.exec(url);

    return !results || !results[2] ? null : results[2].replace(/\+/g, ' ');
  }

  return null;
};

export const queryFilterExport = (qf: string) => toRepresentation(qf);
export const queryFilterImport = (raw: string | null) =>
  new QueryFilter(raw ? fromRepresentation(raw) : null); // eslint-disable-line no-use-before-define

const QS_NAME = 'qf';
const VALID_QUERY_URL_PROP_ORDER = {
  manufacturer: 1,
  modelSeries: 2,
  modelYear: 3,
  spatial: 4,
  gearboxType: 5,
  fuelType: 6,
  fuelTypes: 7,
  engineBaseType: 8,
  color: 9,
  chassis: 10,
  drivingWheel: 11,
  branches: 12,
};

type VALID_QUERY_URL_PROP_ORDER_KEY = keyof typeof VALID_QUERY_URL_PROP_ORDER;

const isPropValidForQueryURL = (propName: VALID_QUERY_URL_PROP_ORDER_KEY, values: string) => {
  if (!VALID_QUERY_URL_PROP_ORDER[propName]) return false;

  if (!values || values.length > 1) return false;

  return true;
};

const fixLocationFilterName = (displayName: string) => {
  return displayName.replace(/, (Sverige|Norge)/g, '');
};

const isPropValidSpatial = (propName: string) =>
  getLocationSet()
    .map((x) => slugify(x.name))
    .some(
      (x) =>
        decodeURIComponent(x) === slugify(decodeURIComponent(propName)) ||
        `${slugify(propName)}`.startsWith(`${x}-`),
    );

const toPropValidSpatial = (propName: string) =>
  getLocationSet()
    .map((x) => slugify(x.name))
    .find(
      (x) =>
        decodeURIComponent(x) === slugify(decodeURIComponent(propName)) ||
        `${slugify(propName)}`.startsWith(`${x}-`),
    );

class QueryFilter {
  query = '';
  params: { [key: string]: any } = {};
  exclusions: { [key: string]: any } = {};

  static OperationalParams = ['hits', 'offset', 'sort', 'includeSoldAtFrom'];

  constructor(raw?: string | null) {
    if (raw) {
      raw
        .replace(/^[\?|&]|&$/g, '')
        .split('&')
        .forEach((keyValue) => {
          const pair = keyValue.split('=');
          if (pair.length === 2) {
            if (pair[0] === 'query' || pair[0] === 'q') {
              this.query = pair[1].replace(/[\*|\:]+/g, '');
            } else if (pair[1].includes('exclude:') || pair[1].includes('exclude%3A')) {
              this.internalExclude(
                pair[0],
                decodeURIComponent(pair[1].replace('exclude:', '').replace('exclude%3A', '')),
              );
            } else {
              this.addPair(pair);
            }
          }
        });
    }
  }

  addPair(pair: any[]) {
    try {
      this.internalAdd(pair[0], decodeURIComponent(pair[1]));
    } catch (err) {
      const isUriError = err instanceof URIError;
      if (isUriError) {
        // eslint-disable-next-line
        console.error(`Could not decode decode pair ${pair[0]}:${pair[1]}. Error: ${err}`);
      } else {
        throw err;
      }
    }
  }

  isEmpty() {
    return (
      !this.query &&
      Object.keys(this.params).length === 0 &&
      Object.keys(this.exclusions).length === 0
    );
  }
  equals(queryFilter: QueryFilter) {
    return this.toString() === queryFilter.toString();
  }
  isSame(queryFilter: QueryFilter, stripOperational = true) {
    const ignoreParams = stripOperational ? QueryFilter.OperationalParams : [];
    return queryFilter && this.normalize(ignoreParams) === queryFilter.normalize(ignoreParams);
  }

  normalize(ignoreParams: string[] = []) {
    const buffer = [this.getQuery().toLowerCase()];
    Object.keys(this.params)
      .sort()
      .forEach((key) => {
        if (ignoreParams && !ignoreParams.includes(key)) {
          buffer.push(
            `${key.toLowerCase()}${(this.params[key] as string[])
              .sort()
              .map((p) => encodeURIComponent(p))
              .join('')
              .toLowerCase()}`,
          );
        }
      });

    Object.keys(this.exclusions)
      .sort()
      .forEach((key) => {
        if (ignoreParams && !ignoreParams.includes(key)) {
          buffer.push(
            `${key.toLowerCase()}exclude:${(this.exclusions[key] as string[])
              .sort()
              .map((p) => encodeURIComponent(p))
              .join('')
              .toLowerCase()}`,
          );
        }
      });

    return buffer.join('');
  }

  removeDefaultSortOption() {
    const self = new QueryFilter(this.toString());
    if (self.hasDefaultSort()) {
      return self.remove('sort');
    }
    return self;
  }

  addQueryFilter(queryFilter: QueryFilter) {
    const self = new QueryFilter(this.toString());

    Object.keys(queryFilter.params).forEach((param) =>
      queryFilter.get(param).forEach((value) => {
        if (!self.contains(param, value)) {
          self.internalAdd(param, value);
        }
      }),
    );
    return self;
  }

  setQuery(query: string) {
    const self = new QueryFilter(this.toString());

    self.query = encodeURIComponent(query);
    return self;
  }
  removeQuery() {
    const self = new QueryFilter(this.toString());

    self.query = '';
    return self;
  }
  hasQuery() {
    return !!this.query;
  }
  getQuery() {
    return decodeURIComponent(this.query);
  }

  internalAdd(prop: string, value: any) {
    if (this.contains(prop, value)) {
      return this;
    }

    if (!this.params[prop]) {
      this.params[prop] = [];
    }

    this.params[prop].push(encodeURIComponent(value));
  }

  internalExclude(prop: string, value: any) {
    if (this.excludes(prop, value)) {
      return this;
    }

    if (!this.exclusions[prop]) {
      this.exclusions[prop] = [];
    }

    this.exclusions[prop].push(encodeURIComponent(value));
  }

  add(prop: string, value: any) {
    const self = new QueryFilter(this.toString());

    if (self.contains(prop, value)) {
      return self;
    }

    if (!self.params[prop]) {
      self.params[prop] = [];
    }

    if (self.excludes(prop, value)) {
      if (self.exclusions[prop].length) {
        self.exclusions[prop].splice(self.exclusions[prop].indexOf(encodeURIComponent(value)), 1);
      } else {
        delete self.exclusions[prop];
      }
    }

    self.params[prop].push(encodeURIComponent(value));
    return self;
  }

  set(prop: string, values: any[]) {
    const self = new QueryFilter(this.toString());
    self.params[prop] = [];
    values.forEach((val) => this.internalAdd(prop, val));
    return self;
  }

  setExclusion(prop: string, values: any[]) {
    const self = new QueryFilter(this.toString());
    self.exclusions[prop] = [];
    values.forEach((val) => this.internalExclude(prop, val));
    return self;
  }

  exclude(prop: string, value: any) {
    const self = new QueryFilter(this.toString());

    if (self.excludes(prop, value)) {
      return self;
    }

    if (!self.exclusions[prop]) {
      self.exclusions[prop] = [];
    }

    if (self.contains(prop, value)) {
      if (self.params[prop].length) {
        self.params[prop].splice(self.params[prop].indexOf(encodeURIComponent(value)), 1);
      } else {
        delete self.params[prop];
      }
    }

    self.exclusions[prop].push(encodeURIComponent(value));
    return self;
  }

  remove(prop: string, value: any = null) {
    const self = new QueryFilter(this.toString());
    if (value) {
      if (!self.params[prop] && !self.exclusions[prop]) {
        return self;
      }
      const paramsFilters = Object.hasOwn(self.params, prop)
        ? (self.params[prop] as string[]).map((f) => decodeURIComponent(f))
        : [];

      const exclusionsFilters = Object.hasOwn(self.exclusions, prop)
        ? (self.exclusions[prop] as string[]).map((f) => decodeURIComponent(f))
        : [];
      const filter = decodeURIComponent(value);

      if (paramsFilters.indexOf(filter) >= 0) {
        self.params[prop].splice(paramsFilters.indexOf(filter), 1);
      }

      if (exclusionsFilters.indexOf(filter) >= 0) {
        self.exclusions[prop].splice(exclusionsFilters.indexOf(filter), 1);
      }
    } else {
      delete self.params[prop];
      delete self.exclusions[prop];
    }
    return self;
  }

  removeAll(props: string[]) {
    const self = new QueryFilter(this.toString());

    props.forEach((prop) => {
      delete self.params[prop];
      delete self.exclusions[prop];
    });

    return self;
  }

  has(prop: string) {
    return Object.keys(this.params).includes(prop);
  }
  hasExclusion(prop: string) {
    return Object.keys(this.exclusions).includes(prop);
  }
  contains(prop: string, value: any) {
    return (this.params[prop] || []).includes(encodeURIComponent(value));
  }
  excludes(prop: string, value: any) {
    return (this.exclusions[prop] || []).includes(encodeURIComponent(value));
  }
  get(prop: string) {
    return ((this.params[prop] || []) as string[]).map((v) => decodeURIComponent(v));
  }
  exclusion(prop: string) {
    return ((this.exclusions[prop] || []) as string[]).map((v) => decodeURIComponent(v));
  }
  getKeys() {
    return Object.keys(this.params)
      .map((p) => p.replace('.min', '').replace('.max', ''))
      .filter((p, index, self) => self.indexOf(p) === index);
  }
  hasFilters() {
    return Object.keys(this.params).length + Object.keys(this.exclusions).length > 0;
  }
  getFilters() {
    return this.toString(false);
  }
  numberOfFilters() {
    const filterCount = (filter: { [key: string]: any }) =>
      Object.keys(filter).reduce((aggregation, next) => {
        aggregation = filter[next].length + aggregation;
        return aggregation;
      }, 0);

    return filterCount(this.params) + filterCount(this.exclusions);
  }

  addRange(prop: string, min?: number | null, max?: number | null) {
    const self = new QueryFilter(this.toString());

    if (min !== null && min !== undefined) {
      self.params[`${prop}.min`] = [min];
    }
    if (max !== null && max !== undefined) {
      self.params[`${prop}.max`] = [max];
    }

    return self;
  }
  removeRange(prop: string) {
    const self = new QueryFilter(this.toString());

    delete self.params[prop];
    delete self.params[`${prop}.min`];
    delete self.params[`${prop}.max`];

    return self;
  }
  hasRange(prop: string) {
    return this.hasRangeMin(prop) || this.hasRangeMax(prop);
  }
  hasRangeMin(prop: string) {
    return this.has(`${prop}.min`);
  }
  hasRangeMax(prop: string) {
    return this.has(`${prop}.max`);
  }
  getRange(prop: string) {
    return {
      min: this.getRangeMin(prop),
      max: this.getRangeMax(prop),
    };
  }
  getRangeMin(prop: string) {
    return (this.get(`${prop}.min`) || []).pop();
  }
  getRangeMax(prop: string) {
    return (this.get(`${prop}.max`) || []).pop();
  }

  addSpatial(spatial: WaykeLocation) {
    if (!spatial || !spatial.radius || !spatial.lat || !spatial.lng) {
      return this;
    }

    const self = new QueryFilter(this.toString());

    self.params['position.location'] = [spatial.radius];
    if (spatial.name) {
      self.params['position.name'] = [encodeURIComponent(fixLocationFilterName(spatial.name))];
    }
    self.params.lat = [spatial.lat];
    self.params.lon = [spatial.lng];

    return self;
  }
  getSpatial(): WaykeQueryLocation | null {
    return this.hasSpatial()
      ? {
          distance: Number.parseInt(this.params['position.location'][0] as string, 10),
          name: decodeURIComponent(this.params['position.name'][0]),
          lat: Number.parseFloat(this.params.lat[0] as string),
          lng: Number.parseFloat(this.params.lon[0] as string),
        }
      : null;
  }
  getSpatialName() {
    return decodeURIComponent(this.params['position.name'][0]);
  }
  getSpatialDistance() {
    return this.params['position.location'][0];
  }
  hasSpatial() {
    return this.has('position.location');
  }
  removeSpatial() {
    const self = new QueryFilter(this.toString());

    delete self.params['position.location'];
    delete self.params['position.name'];
    delete self.params.lat;
    delete self.params.lon;

    return self;
  }
  setPaginationSize = (offset: number) => {
    const self = new QueryFilter(this.toString());

    self.internalAdd('hits', `${offset}`);

    return self;
  };
  hasPagination() {
    return this.has('offset');
  }

  hasDefaultSort() {
    return this.has('sort') && this.get('sort')?.[0] === 'itemSortDesc';
  }

  removePagination() {
    return this.removeAll(['page', 'hits', 'offset']);
  }

  static create(queryFilterString?: string) {
    return new QueryFilter(queryFilterString);
  }

  static merge(queryFilter1: QueryFilter, queryFilter2: QueryFilter) {
    const str = `${queryFilter1 ? queryFilter1.toString(queryFilter1.hasQuery()) : ''}&${
      queryFilter2 ? queryFilter2.toString(!queryFilter1.hasQuery()) : ''
    }`;
    return new QueryFilter(str.replace(/^&|&$/g, ''));
  }

  static byQuery(query: string) {
    const qf = new QueryFilter();
    qf.query = encodeURIComponent(query);

    return qf;
  }

  static byProperty(prop: string, value: any) {
    const qf = new QueryFilter();
    qf.internalAdd(prop, value);

    return qf;
  }

  static fromRepresentation(raw: string | null) {
    return queryFilterImport(raw);
  }

  static fromQueryString(url?: string) {
    if (!process.env.SSR) {
      let raw = (window || {}).location ? queryString(QS_NAME) : null;
      if (url) {
        raw = queryString(QS_NAME, url);
      }
      if (raw) return queryFilterImport(raw);
    }

    return new QueryFilter();
  }

  toString(includeQuery = true) {
    const buffer: string[] = [];

    if (this.hasQuery() && includeQuery) {
      buffer.push(`q=${this.query}`);
    }

    Object.keys(this.params)
      .sort((a, b) => a.localeCompare(b))
      .forEach((param) =>
        (this.params[param] as any[])
          .sort((a, b) => a.localeCompare(b))
          .forEach((value) => buffer.push(`${param}=${value}`)),
      );
    Object.keys(this.exclusions)
      .sort((a, b) => a.localeCompare(b))
      .forEach((exclusion) =>
        (this.exclusions[exclusion] as any[])
          .sort((a, b) => a.localeCompare(b))
          .forEach((value) => buffer.push(`${exclusion}=exclude:${value}`)),
      );

    return buffer.join('&');
  }

  toQueryString() {
    if (this.hasQuery() || this.hasFilters()) {
      return `?${QS_NAME}=${queryFilterExport(this.toString())}`;
    }

    return '';
  }

  toQueryURL() {
    const self = new QueryFilter(this.toString());
    let clean = self.removeRange('modelYear').removeSpatial();
    const forceQueryFilter =
      clean.hasQuery() ||
      Object.keys(clean.params).filter(
        (key) => !isPropValidForQueryURL(key as VALID_QUERY_URL_PROP_ORDER_KEY, clean.params[key]),
      ).length > 0 ||
      Object.keys(self.exclusions).length > 0;
    if (forceQueryFilter) return this.toQueryString();

    if (
      self.hasSpatial() &&
      isPropValidSpatial(self.getSpatialName()) &&
      (getMarket().marketCode === 'NO'
        ? self.getSpatialDistance() === '100'
        : self.getSpatialDistance() === '50')
    ) {
      clean = clean.add('spatial', toPropValidSpatial(self.getSpatialName()));
    } else if (self.hasSpatial()) {
      return this.toQueryString();
    }

    if (
      self.hasRange('modelYear') &&
      self.getRangeMin('modelYear') === self.getRangeMax('modelYear')
    ) {
      clean = clean.add('modelYear', self.getRangeMin('modelYear'));
    } else if (self.hasRange('modelYear')) {
      return this.toQueryString();
    }

    const urlParams = Object.keys(clean.params)
      .sort(
        (a, b) =>
          VALID_QUERY_URL_PROP_ORDER[a as VALID_QUERY_URL_PROP_ORDER_KEY] -
          VALID_QUERY_URL_PROP_ORDER[b as VALID_QUERY_URL_PROP_ORDER_KEY],
      )
      .map((key) => slugify(decodeURIComponent(clean.params[key].pop())));

    const urlExclusions = Object.keys(clean.exclusions)
      .sort(
        (a, b) =>
          VALID_QUERY_URL_PROP_ORDER[a as VALID_QUERY_URL_PROP_ORDER_KEY] -
          VALID_QUERY_URL_PROP_ORDER[b as VALID_QUERY_URL_PROP_ORDER_KEY],
      )
      .map((key) => slugify(decodeURIComponent(clean.exclusions[key].pop())));

    const url = urlParams.concat(urlExclusions).join('/');

    return `/${url}`;
  }

  toSearchQuery() {
    return toRepresentation(`q=*${this.hasFilters() ? `&${this.toString(false)}` : ''}`);
  }

  toQueryStringFilter() {
    return toRepresentation(this.toString());
  }
}

export default QueryFilter;
